import React from "react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";

type Props = {
  status: string;
  message?: string | null;
  fileName?: string | null;

  totalRows: number;
  processedRows: number;
  parsedCount: number;
  insertedCount: number;
  dupFile: number;
  dupDb: number;
};

const prettyStatus = (s: string) => {
  switch ((s || "").toLowerCase()) {
    case "queued":
      return "Starting…";
    case "parsing":
      return "Reading your file…";
    case "deduping":
      return "Checking for duplicates…";
    case "inserting":
      return "Saving transactions…";
    case "done":
      return "Import completed";
    case "error":
      return "Something went wrong";
    default:
      return "Working…";
  }
};

export const ImportProgressPanel: React.FC<Props> = ({
  status,
  message,
  fileName,
  totalRows,
  processedRows,
  parsedCount,
  insertedCount,
  dupFile,
  dupDb,
}) => {
  const pct =
    totalRows > 0
      ? Math.min(100, Math.round((processedRows / totalRows) * 100))
      : 0;

  const isDone = (status || "").toLowerCase() === "done";
  const isError = (status || "").toLowerCase() === "error";

  return (
    <div className="space-y-3 rounded-lg border p-3 bg-muted/20">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Badge variant="outline">{prettyStatus(status)}</Badge>
            {fileName ? (
              <span className="text-xs text-muted-foreground truncate">
                {fileName}
              </span>
            ) : null}
          </div>

          {message ? (
            <div className="text-xs text-muted-foreground mt-1">
              {message}
            </div>
          ) : null}
        </div>

        <div className="text-xs tabular-nums text-muted-foreground">
          {totalRows > 0 ? `${processedRows}/${totalRows}` : "—"}
        </div>
      </div>

      <Progress value={pct} />

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex justify-between">
          <span className="text-muted-foreground">
            Transactions found
          </span>
          <span className="tabular-nums">{parsedCount}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-muted-foreground">
            New transactions added
          </span>
          <span className="tabular-nums">{insertedCount}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-muted-foreground">
            Duplicates (in file)
          </span>
          <span className="tabular-nums">{dupFile}</span>
        </div>

        <div className="flex justify-between">
          <span className="text-muted-foreground">
            Duplicates (already imported)
          </span>
          <span className="tabular-nums">{dupDb}</span>
        </div>
      </div>

      {isDone && (
        <div className="text-xs text-green-600 pt-1">
          Import finished. You can close this window — the transactions will appear in the table.
        </div>
      )}

      {isError && (
        <div className="text-xs text-red-600 pt-1">
          Import failed. Please try again or contact support.
        </div>
      )}
    </div>
  );
};
